import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/base/sheet";

import { useDebugContext } from "./use_debug_context";
import { OverlayRow, OverlaySection } from "./OverlaySection";

/**
 * Cognito account state, and the claims the merchant's next token will
 * carry.
 *
 * Loads on open rather than with the Profile overlay that links to it: the
 * query behind it reaches Cognito and Amplitude, which nobody should pay
 * for unless they asked this question.
 */
export function AuthSheet({
  open,
  onOpenChange,
  businessId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
}) {
  const { context, error, loading } = useDebugContext(businessId, open);
  const authState = context?.authState ?? null;
  const claims = context?.projectedClaims ?? null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-[563px]">
        <SheetHeader className="border-border border-b px-6 py-4">
          <SheetTitle className="text-foreground text-lg font-semibold">
            Auth
          </SheetTitle>
        </SheetHeader>

        <div className="overflow-y-auto">
          {error ? (
            <p className="px-6 py-5 text-sm text-red-600">
              Could not load: {error}
            </p>
          ) : loading ? (
            <p className="text-subtle-foreground px-6 py-5 text-sm">Loading…</p>
          ) : (
            <>
              <OverlaySection title="Cognito">
                {authState?.unavailableReason ? (
                  <p className="text-sm text-amber-700">
                    {authState.unavailableReason}
                  </p>
                ) : authState ? (
                  <>
                    <OverlayRow label="Status">{authState.status}</OverlayRow>
                    <OverlayRow label="Enabled">
                      {authState.enabled === null
                        ? null
                        : String(authState.enabled)}
                    </OverlayRow>
                    <OverlayRow label="MFA">
                      {authState.mfaEnabled === null
                        ? null
                        : authState.mfaEnabled
                          ? "on"
                          : "off"}
                    </OverlayRow>
                    <OverlayRow label="Groups">
                      {authState.groupsUnavailable
                        ? "unreadable"
                        : authState.groups.join(", ") || null}
                    </OverlayRow>
                    <OverlayRow label="Created">
                      {authState.createdAt
                        ? new Date(authState.createdAt).toLocaleString()
                        : null}
                    </OverlayRow>
                    <OverlayRow label="Last modified">
                      {authState.lastModifiedAt
                        ? new Date(authState.lastModifiedAt).toLocaleString()
                        : null}
                    </OverlayRow>
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    No user on this business.
                  </p>
                )}
              </OverlaySection>

              <OverlaySection title="Claims on their next token">
                {claims ? (
                  <>
                    <OverlayRow label="businessId">
                      {claims.businessId}
                    </OverlayRow>
                    <OverlayRow label="businessName">
                      {claims.businessName}
                    </OverlayRow>
                    <OverlayRow label="subscription">
                      {claims.subscriptionStatus}
                    </OverlayRow>
                    <OverlayRow label="plan">{claims.plan}</OverlayRow>
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Claims could not be projected.
                  </p>
                )}
              </OverlaySection>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
